import React, { useState, useMemo, useRef, useEffect } from 'react';
import { api } from '../api';
import Landing from './Landing';
import { COUNTRIES } from '../countries';
import { DIAL_CODES, DEFAULT_DIAL, findDialByCountryName } from '../dialCodes';

const EXPERIENCE_OPTIONS = [
  'None',
  'Beginner (under 1 year)',
  'Intermediate (1-3 years)',
  'Advanced (3+ years)'
];

const SAVINGS_OPTIONS = [
  'Under $2.5k',
  '$5k - $10k',
  '$10k - $25k',
  '$25k - $50k',
  '$100k - $250k',
  '$250k - $500k',
  '$1mil+'
];

const VIP_OPTIONS = [
  'Current VIP',
  'Previous VIP',
  'Never been a member'
];

const STEPS = [
  { key: 'name', label: 'What is your full name?', type: 'text', placeholder: 'Your full name' },
  { key: 'telegram_handle', label: 'What is your Telegram handle?', type: 'text', placeholder: '@yourhandle' },
  { key: 'phone', label: 'What is your phone number?', type: 'phone', placeholder: '555 555 5555', hint: 'For backup contact only, calls happen on Telegram.' },
  { key: 'email', label: 'What is your email address?', type: 'email', placeholder: 'you@example.com' },
  { key: 'country', label: 'Where are you located?', type: 'country' },
  { key: 'crypto_experience', label: 'How much crypto experience do you have?', type: 'select', options: EXPERIENCE_OPTIONS },
  { key: 'savings_range', label: 'Approximately how much can you invest?', type: 'select', options: SAVINGS_OPTIONS },
  { key: 'vip_status', label: 'Are you currently or were you previously an Elite Edge VIP member?', type: 'select', options: VIP_OPTIONS },
  { key: 'has_telegram', label: 'Do you have Telegram installed?', type: 'yesno', hint: 'Telegram is required to use Heilpern Futures.' }
];

export default function Apply() {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (started && !done) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [step, started, done]);

  const current = STEPS[step];
  const total = STEPS.length;
  const progress = useMemo(() => Math.round((step / total) * 100), [step, total]);
  const value = answers[current?.key] ?? '';

  const canAdvance = useMemo(() => {
    if (!current) return false;
    if (current.type === 'yesno') return value === true || value === false;
    return String(value).trim().length > 0;
  }, [current, value]);

  function setValue(v) {
    setAnswers(a => ({ ...a, [current.key]: v }));
  }

  async function handleNext() {
    if (!canAdvance || submitting) return;
    if (step < total - 1) {
      setStep(step + 1);
    } else {
      try {
        setSubmitting(true);
        setError('');
        await api.submitLead(answers);
        setDone(true);
      } catch (err) {
        setError('Could not submit. Please check your connection and try again.');
      } finally {
        setSubmitting(false);
      }
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && current?.type !== 'yesno') {
      e.preventDefault();
      handleNext();
    }
  }

  if (done) {
    return (
      <div className="apply-shell">
        <div className="apply-card success">
          <div className="success-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h1>Thank you{answers.name ? `, ${String(answers.name).split(' ')[0]}` : ''}.</h1>
          <p>Your application has been received.</p>
          <p className="muted">A member of our team will reach out on Telegram within 24 hours to schedule your discovery call. Please check your email for confirmation.</p>
        </div>
      </div>
    );
  }

  if (!started) {
    return <Landing onApply={() => { setStarted(true); window.scrollTo(0, 0); }} />;
  }

  return (
    <div className="apply-shell">
      <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
      <div className="apply-card">
        <div className="step-counter">Question {step + 1} of {total}</div>
        <h2 className="step-label">{current.label}</h2>
        {current.hint && <p className="step-hint">{current.hint}</p>}

        <div className="step-input">
          {current.type === 'phone' ? (
            <PhoneInput
              value={value}
              onChange={setValue}
              defaultDial={findDialByCountryName(answers.country) || DEFAULT_DIAL}
              onSubmit={handleNext}
              inputRef={inputRef}
            />
          ) : current.type === 'text' || current.type === 'email' || current.type === 'tel' ? (
            <input
              ref={inputRef}
              className="text-input"
              type={current.type}
              placeholder={current.placeholder}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete={current.key === 'email' ? 'email' : current.key === 'phone' ? 'tel' : 'off'}
            />
          ) : current.type === 'select' ? (
            <div className="option-grid">
              {current.options.map(opt => (
                <button
                  key={opt}
                  className={`option-btn ${value === opt ? 'selected' : ''}`}
                  onClick={() => {
                    setValue(opt);
                    if (step < total - 1) setStep(step + 1);
                    else setTimeout(handleNext, 50);
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : current.type === 'country' ? (
            <CountrySearch
              value={value}
              onChange={setValue}
              onSelect={() => { if (step < total - 1) setStep(step + 1); }}
              inputRef={inputRef}
            />
          ) : current.type === 'yesno' ? (
            <div className="option-grid two-col">
              <button
                className={`option-btn ${value === true ? 'selected' : ''}`}
                onClick={() => {
                  setValue(true);
                  if (step < total - 1) setStep(step + 1);
                  else setTimeout(handleNext, 50);
                }}
              >Yes</button>
              <button
                className={`option-btn ${value === false ? 'selected' : ''}`}
                onClick={() => { setValue(false); }}
              >No</button>
              {value === false && (
                <div className="soft-warning">
                  Telegram is required to use Heilpern Futures. Please install it before your discovery call.
                </div>
              )}
            </div>
          ) : null}
        </div>

        {error && <div className="error-msg">{error}</div>}

        <div className="step-nav">
          {step > 0 && <button className="btn-ghost" onClick={() => setStep(step - 1)}>Back</button>}
          {(current.type === 'text' || current.type === 'email' || current.type === 'tel' || current.type === 'phone' || (current.type === 'yesno' && value === false)) && (
            <button className="btn-primary" onClick={handleNext} disabled={!canAdvance || submitting}>
              {submitting ? 'Submitting...' : (step === total - 1 ? 'Submit application' : 'Next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CountrySearch({ value, onChange, onSelect, inputRef }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    if (!query) return COUNTRIES.slice(0, 8);
    const q = query.toLowerCase();
    return COUNTRIES.filter(c => c.toLowerCase().includes(q)).slice(0, 8);
  }, [query]);

  return (
    <div className="country-search">
      <input
        ref={inputRef}
        className="text-input"
        type="text"
        placeholder="Type to search..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(''); }}
        onFocus={() => setOpen(true)}
      />
      {open && matches.length > 0 && (
        <div className="country-dropdown">
          {matches.map(c => (
            <button
              key={c}
              className="country-option"
              onClick={() => {
                setQuery(c);
                onChange(c);
                setOpen(false);
                onSelect && onSelect();
              }}
            >{c}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange, defaultDial, onSubmit, inputRef }) {
  // value is a single string like "+1 5551234567"; split into dial + number
  const [dial, setDial] = useState(() => {
    if (!value) return defaultDial;
    const match = DIAL_CODES.find(c => value.startsWith(c.dial));
    return match || defaultDial;
  });
  const [number, setNumber] = useState(() => {
    if (!value) return '';
    const match = DIAL_CODES.find(c => value.startsWith(c.dial));
    return match ? value.slice(match.dial.length).trim() : value;
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  function emit(d, n) {
    const composed = n ? `${d.dial} ${n}`.trim() : '';
    onChange(composed);
  }

  function pickDial(d) {
    setDial(d);
    setOpen(false);
    setSearch('');
    emit(d, number);
  }

  const filtered = useMemo(() => {
    if (!search) return DIAL_CODES;
    const q = search.toLowerCase();
    return DIAL_CODES.filter(c =>
      c.name.toLowerCase().includes(q) || c.dial.includes(search) || c.code.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <div className="phone-input">
      <div className="phone-row">
        <button type="button" className="phone-dial-btn" onClick={() => setOpen(o => !o)}>
          <span className="phone-flag">{dial.flag}</span>
          <span className="phone-dial">{dial.dial}</span>
          <span className="phone-chevron">▾</span>
        </button>
        <input
          ref={inputRef}
          className="text-input phone-number"
          type="tel"
          placeholder="555 555 5555"
          value={number}
          onChange={e => { const n = e.target.value; setNumber(n); emit(dial, n); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSubmit && onSubmit(); } }}
        />
      </div>
      {open && (
        <div className="phone-dropdown">
          <input
            autoFocus
            className="text-input phone-search"
            placeholder="Search country or code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="phone-list">
            {filtered.map(c => (
              <button
                type="button"
                key={c.code + c.dial}
                className="phone-option"
                onClick={() => pickDial(c)}
              >
                <span className="phone-flag">{c.flag}</span>
                <span>{c.name}</span>
                <span className="phone-dial muted">{c.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && <div className="phone-empty">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}
